/**
 * CostTrackerPanel.jsx — Cost Tracker v2.1 (iter270.4)
 * Admin-only live cost analytics tile.
 * Polls /api/admin/cost/summary every 30s and surfaces:
 *   • today's total cost + day-over-day delta
 *   • per-event-type breakdown
 *   • warnings (red chips) when thresholds tripped
 *   • instrumentation coverage banner (partial vs complete)
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

function formatUSD(n) {
  return `$${Number(n || 0).toFixed(4)}`;
}

function formatPct(n) {
  if (n === null || n === undefined || n === "N/A") return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${Number(n).toFixed(1)}%`;
}

function MetricCard({ label, value, sub, testid }) {
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white px-4 py-3"
      data-testid={testid}
    >
      <div className="text-[11px] uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-xl font-semibold text-slate-900">{value}</div>
      {sub ? (
        <div className="mt-1 text-[11px] text-slate-500">{sub}</div>
      ) : null}
    </div>
  );
}

export default function CostTrackerPanel() {
  const [data, setData] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [error, setError] = useState(null);

  const fetchAll = async () => {
    try {
      const token =
        localStorage.getItem("admin_token") || localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [s, c] = await Promise.all([
        axios.get(`${API}/api/admin/cost/summary`, { headers }),
        axios.get(`${API}/api/admin/cost/coverage`, { headers }),
      ]);
      setData(s.data);
      setCoverage(c.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 30000);
    return () => clearInterval(id);
  }, []);

  if (error) {
    return (
      <div
        className="p-4 text-sm text-red-600"
        data-testid="cost-tracker-error"
      >
        Failed to load cost data: {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div
        className="p-4 text-sm text-slate-500"
        data-testid="cost-tracker-loading"
      >
        Loading cost tracker…
      </div>
    );
  }

  const totals = data?.realtime?.totals || {};
  const costs = data?.realtime?.costs || {};
  const dod = data?.day_over_day || {};
  const warnings = data?.warnings || [];
  const breakdown = data?.realtime?.breakdowns?.by_event_type || {};

  return (
    <div className="space-y-4 p-4" data-testid="cost-tracker-panel">
      {/* Header */}
      <div className="flex items-baseline justify-between">
        <h3 className="text-base font-semibold text-slate-900">
          Live Cost Tracker
        </h3>
        <span className="text-[11px] text-slate-500">
          Business day {data.business_day}
        </span>
      </div>

      {/* Partial-instrumentation banner */}
      {data.partial_instrumentation_notice ? (
        <div
          className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-800"
          data-testid="cost-tracker-partial-notice"
        >
          {data.partial_instrumentation_notice}{" "}
          {coverage?.health ? (
            <span className="font-medium">
              ({coverage.health.counts.active}/{coverage.health.total} paths
              active · {coverage.health.coverage_percent}% coverage)
            </span>
          ) : null}
        </div>
      ) : null}

      {/* Top metric cards */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <MetricCard
          testid="cost-tracker-total-cost"
          label="Total cost (24h)"
          value={formatUSD(costs.total)}
          sub={`Δ ${formatPct(dod.total_cost)} vs yesterday`}
        />
        <MetricCard
          testid="cost-tracker-monthly-runrate"
          label="Monthly run rate"
          value={`$${Number(costs.monthly_run_rate || 0).toFixed(2)}`}
          sub="× 30 days projection"
        />
        <MetricCard
          testid="cost-tracker-active-users"
          label="Active users"
          value={totals.active_users || 0}
          sub={`Δ ${formatPct(dod.active_users)}`}
        />
        <MetricCard
          testid="cost-tracker-events"
          label="Events tracked"
          value={totals.events || 0}
          sub={`Δ ${formatPct(dod.events)}`}
        />
      </div>

      {/* Cost breakdown */}
      <div
        className="rounded-lg border border-slate-200 bg-white p-3"
        data-testid="cost-tracker-cost-breakdown"
      >
        <div className="mb-2 text-[11px] uppercase tracking-wider text-slate-500">
          Cost breakdown (24h)
        </div>
        <div className="grid grid-cols-2 gap-2 text-[13px] md:grid-cols-4">
          <div>
            <span className="text-slate-500">Hosting</span>{" "}
            <span className="font-medium">{formatUSD(costs.hosting_daily)}</span>
          </div>
          <div>
            <span className="text-slate-500">API</span>{" "}
            <span className="font-medium">{formatUSD(costs.api_total)}</span>
          </div>
          <div>
            <span className="text-slate-500">Email</span>{" "}
            <span className="font-medium">{formatUSD(costs.email_total)}</span>
          </div>
          <div>
            <span className="text-slate-500">AI tokens</span>{" "}
            <span className="font-medium">{formatUSD(costs.token_total)}</span>
          </div>
        </div>
      </div>

      {/* Event-type breakdown */}
      <div
        className="rounded-lg border border-slate-200 bg-white p-3"
        data-testid="cost-tracker-event-breakdown"
      >
        <div className="mb-2 text-[11px] uppercase tracking-wider text-slate-500">
          Events by type (24h)
        </div>
        {Object.keys(breakdown).length === 0 ? (
          <div className="text-[13px] text-slate-400">
            No events tracked yet.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[13px] md:grid-cols-3">
            {Object.entries(breakdown)
              .sort(([, a], [, b]) => b - a)
              .map(([k, v]) => (
                <div key={k} className="flex justify-between">
                  <span className="text-slate-600">{k}</span>
                  <span className="font-medium tabular-nums">{v}</span>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Warnings */}
      {warnings.length > 0 ? (
        <div
          className="rounded-lg border border-red-200 bg-red-50 p-3"
          data-testid="cost-tracker-warnings"
        >
          <div className="mb-1 text-[11px] uppercase tracking-wider text-red-700">
            Warnings ({warnings.length})
          </div>
          <ul className="space-y-1 text-[13px] text-red-800">
            {warnings.map((w) => (
              <li
                key={w.key}
                data-testid={`cost-tracker-warning-${w.key}`}
              >
                {w.label} — value {w.value}, threshold {w.threshold}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Coverage matrix */}
      {coverage?.coverage ? (
        <details
          className="rounded-lg border border-slate-200 bg-white p-3"
          data-testid="cost-tracker-coverage-details"
        >
          <summary className="cursor-pointer text-[12px] font-medium text-slate-700">
            Instrumentation coverage ({coverage.health?.counts?.active || 0}{" "}
            active · {coverage.health?.counts?.pending || 0} pending)
          </summary>
          <ul className="mt-2 space-y-1 text-[12px]">
            {coverage.coverage.map((c) => (
              <li
                key={c.path_id}
                className="flex justify-between"
                data-testid={`cost-coverage-row-${c.path_id}`}
              >
                <span>
                  <span
                    className={
                      c.status === "active"
                        ? "text-emerald-700"
                        : "text-amber-700"
                    }
                  >
                    ●
                  </span>{" "}
                  {c.label}
                </span>
                <span className="text-slate-500">
                  {c.status} · {c.target_version}
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {/* Failures footer */}
      {data.tracking_failures_today > 0 ? (
        <div
          className="text-[11px] text-amber-700"
          data-testid="cost-tracker-failures-today"
        >
          {data.tracking_failures_today} tracking failure(s) today.
        </div>
      ) : null}
    </div>
  );
}
